import { IFileMetadata } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/metadata/file-metadata';
import { IImageBasicMetadata } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/metadata/image-basic-metadata';
import { IListFilesResponse } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/responses/list-files-response';
import {
  FileType,
  MediaType,
} from '@wix/media-platform-js-client/dist/mjs/src/types/media-platform/media-platform';

import { TableSorting } from '../redux/sorted-columns/sorted-columns.reducer';

import { FileDescriptor, MPCMimeType, Project } from './entities';
import { MaybeUndef } from './system';

/**
 * This types extends FileDescriptor and adds additional props to be used in UI.
 */
export interface MediaAsset extends FileDescriptor {
  isFolder: boolean;
  clientName: string;
  mimeTypeParsed: MPCMimeType;
}

export interface GroupFilesUploadingPayload {
  groupId: string;
  clientFileIds: string[];
}

export interface SingleFileUploadPayload {
  id: string;
  path: string;
  file: File;
}

export interface UploadingFile {
  id: string;
  path: string;
  name: string;
  size: number;
  dateCreated: string;
  progress: number;
}

export interface LoadFilesRequestData {
  path: string;
  organizationId: string;
  project: Project;
  sort?: MaybeUndef<TableSorting>;
  type?: FileType;
}

export interface NavigateToFolder extends LoadFilesRequestData {
  locateFileId: MaybeUndef<string>;
}

export enum BrowserAssetType {
  ExistingFile,
  UploadingFile,
}

export interface BrowserAssetExisting {
  type: BrowserAssetType.ExistingFile;
  asset: MediaAsset;
}

export interface BrowserAssetUploadingFile {
  type: BrowserAssetType.UploadingFile;
  file: UploadingFile;
}

export type BrowserAsset = BrowserAssetExisting | BrowserAssetUploadingFile;

export interface UploadErrorActionPayload {
  clientId: string; // there is some more of additional data...
  file: File;
  path: string;
}

export interface UploadFileSuccessPayload {
  clientId: string;
  file: MediaAsset;
  parentFolderPath: string;
}

export interface GetFilesResponse {
  path: string;
  files: MediaAsset[];
  nextPageToken?: string;
}

export interface OpenFolderResponse extends GetFilesResponse {
  locateFileId: MaybeUndef<string>;
}

export interface UploadSuccessEventData {
  fileDescriptors: FileDescriptor[];
}

/* export interface ExtractedFilesReport {
  destination: Destination;
  format: 'csv' | 'json';
}

export interface ArchiveRequestData {
  source: Source;
  destination: Destination;
  archiveType: string;
}*/

/* export interface CreateArchiveResponsePayload {
  id: string; // job id
  groupId: string; // job group id
  status: JobStatus;
  issuer: string; // "urn:app:<app-id>",
  type: string; // "urn:job:archive.extract",
  specification: {
    source: Source;
    destination: Destination;
  };
  // appears only if report was requested
  extractedFilesReport?: ExtractedFilesReport;

  dateUpdated: string;
  dateCreated: string;
}*/

export interface GetFilesListResponse extends IListFilesResponse {
  files: FileDescriptor[];
}

export interface AudioStreamMetadata {
  codecLongName: string; // 'AAC (Advanced Audio Coding)'
  index: number; // 1
  codecTag: number; // 'mp4a',
  codecName: string; // 'aac',
  duration: number; // 59351,
  bitrate: number; // 128322,
  channels?: number; // Can be null because this field was added recently
  sampleRate?: number; // Can be null because this field was added recently
}

export interface VideoStreamMetadata {
  codecLongName: string; // 'MPEG-4 part 2'
  index: number; // 0
  rotate: number; // 0
  rFrameRate: string; // '30000/1001'
  codecTag: string; // 'mp4v'
  avgFrameRate: string; // '2997/100'
  codecName: string; // 'mpeg4'
  sampleAspectRatio: string; // '1:1'
  height: number; // 720
  width: number; // 1280
  pixelFormat: string; // 'yuv420p'
  duration: number; // 59351
  bitrate: number; // 1950467
  displayAspectRatio: string; // '16:9'
}

export enum MetadataMediaType {
  Video = 'video',
  Image = 'image',
}

export interface VideoFileMetadata {
  fileDescriptor: FileDescriptor;
  basic: {
    interlaced: boolean; // False
    tbr?: number; // 29, None
    videoStreams: VideoStreamMetadata[];
    audioStreams: AudioStreamMetadata[];
    format: {
      duration: number; // 59351
      bitrate: number; // 2085272
      formatLongName: string; // 'QuickTime / MOV',
      size: number; // 15476893
    };
  };
  posters: {
    width: number; // 640
    height: number; // 360
    format: string; // "jpg"
    urls: string[]; // "/lux tamil 12 sec.mp4.output/posters/b832860e143441179fb422432acc11eb000.jpg"
  };
  mediaType: MediaType.Video; // This type is a const for VideoFileMetadata
}

export interface ImageFileMetadata extends IFileMetadata {
  basic: IImageBasicMetadata;
  mediaType: MediaType.Image; // This type is a const for ImageFileMetadata
}

/**
 * This is a generic type for all metadata types.
 * "mediaType" must be used as a disjoint union key.
 * TODO: Add Audio type
 */
export type FileMetadata = VideoFileMetadata | ImageFileMetadata;

export const isImageMetadata = (
  metadata: FileMetadata,
): metadata is ImageFileMetadata => metadata.mediaType === MediaType.Image;

export interface ImageTokenResponse {
  token: string;
}
